'use client';

// Phase-1 minimal billing page (per docs/pricing-strategy-v2.md):
//   The full tier grid (Free / Team / Club / Growth / Elite / Enterprise)
//   is intentionally not built until Phase 3. Today we only sell the
//   Founding Club offer via direct founder contact + manual Stripe invoice.
//   This page therefore shows: current plan label + (if subscribed)
//   Stripe portal link + a single CTA to the Founding Club pitch.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import usePlatformClaims from '@/hooks/usePlatformClaims';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import clsx from 'clsx';
import {
  CreditCardIcon,
  SparklesIcon,
  ArrowUpRightIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

function SectionLabel({ children }) {
  return (
    <div
      className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </div>
  );
}

function SectionCard({ children, title, icon: Icon, description }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
      <div className="border-b border-zinc-100 px-6 py-4 flex items-start gap-3">
        <div className="p-2 rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]">
          {Icon && <Icon className="w-5 h-5" />}
        </div>
        <div>
          <h3
            className="text-base font-bold uppercase tracking-wide text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {title}
          </h3>
          {description && (
            <p className="mt-0.5 text-xs text-zinc-500">{description}</p>
          )}
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

function PlanPill({ tone, children }) {
  const palette = {
    free: 'bg-zinc-100 text-zinc-700 ring-zinc-200',
    founding: 'bg-[#0d1f14] text-[#b5f03d] ring-[#0d1f14]',
  }[tone] || 'bg-zinc-100 text-zinc-700 ring-zinc-200';

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1',
        palette
      )}
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </span>
  );
}

export default function BillingSettings() {
  const { userProfile, firebaseUser } = useAuth();
  // Platform staff (founder / support) get paid-tier access automatically.
  // Read off the JWT — userProfile.role is client-writable.
  const { isFounder, isSupport } = usePlatformClaims();
  const navigate = useNavigate();
  const [entitlement, setEntitlement] = useState(null);

  useEffect(() => {
    async function fetchEntitlements() {
      if (!firebaseUser?.uid) return;
      try {
        const entRef = doc(db, 'users', firebaseUser.uid, 'entitlements', 'app');
        const snap = await getDoc(entRef);
        if (snap.exists()) setEntitlement(snap.data());
      } catch (err) {
        console.error('Error fetching plan:', err);
      }
    }
    fetchEntitlements();
  }, [firebaseUser]);

  // Phase 1: any paid plan = "Founding Club". Tier grid lands in Phase 3.
  const rootPlan = userProfile?.plan?.toLowerCase() || '';
  const subPlan = entitlement?.plan?.toLowerCase() || '';
  const hasPaidPlan =
    ['coach', 'club', 'pro', 'growth', 'team', 'starter', 'elite', 'enterprise', 'founding'].includes(subPlan) ||
    ['coach', 'club', 'pro', 'growth', 'team', 'starter', 'elite', 'enterprise', 'founding'].includes(rootPlan) ||
    isFounder ||
    isSupport;

  const planLabel = hasPaidPlan ? 'Founding Club' : 'Free';
  const planTone = hasPaidPlan ? 'founding' : 'free';
  const planBlurb = hasPaidPlan
    ? 'Locked-in early access — direct founder support, full feature set.'
    : 'Solo coaches & single rec teams. Core roster and chat included, no card required.';

  return (
    <div className="mx-auto max-w-2xl space-y-8 animate-in fade-in duration-500 pt-2 pb-20">
      {/* Header */}
      <div>
        <SectionLabel>Settings</SectionLabel>
        <div className="mt-1 flex items-center gap-3 mb-1">
          <h1
            className="text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Billing
          </h1>
          <PlanPill tone={planTone}>{planLabel}</PlanPill>
        </div>
        <p className="mt-1 text-sm text-zinc-500">
          Your current plan and how to talk to us about it.
        </p>
      </div>

      {/* Current plan */}
      <SectionCard title="Current Plan" icon={CreditCardIcon}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4
                className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {planLabel}
              </h4>
            </div>
            <p className="text-xs text-zinc-500">{planBlurb}</p>
          </div>
        </div>

        {hasPaidPlan && (
          <div className="mt-6 pt-5 border-t border-zinc-100 text-xs text-zinc-500">
            <p>Need to update your card or change plans?</p>
            <p>
              Billing is handled directly for now —{' '}
              <a
                href="mailto:hello@getscrimly.com"
                className="font-semibold text-[#3d6010] hover:underline"
              >
                hello@getscrimly.com
              </a>
              .
            </p>
          </div>
        )}
      </SectionCard>

      {/* Talk to founder CTA — Phase 1 sales channel */}
      {!hasPaidPlan && (
        <div className="rounded-2xl p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] bg-gradient-to-r from-[#b5f03d] to-[#94cc28] text-[#0d1f14]">
          <div className="flex items-start justify-between gap-4 flex-col sm:flex-row">
            <div className="flex items-start gap-3">
              <SparklesIcon className="h-7 w-7 flex-shrink-0" />
              <div>
                <p
                  className="font-bold text-base leading-tight uppercase tracking-wide"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  Founding Club Program
                </p>
                <p className="text-sm opacity-80 mt-0.5">
                  Locked-in pricing for early clubs — direct line to the founder, full access, free migration.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/billing/upgrade')}
              className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-bold uppercase tracking-wide bg-[#0d1f14] text-[#b5f03d] hover:bg-[#1a3622] whitespace-nowrap transition-all"
              style={{ fontFamily: HEAD_FONT }}
            >
              Talk to the Founder
              <ArrowUpRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Quiet sales line — surfaces even on paid plans */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 flex items-start gap-3">
        <ChatBubbleLeftRightIcon className="h-5 w-5 flex-shrink-0 text-[#5a8a1f] mt-0.5" />
        <div className="flex-1">
          <p
            className="text-sm font-bold uppercase tracking-wide text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Questions about pricing?
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            Self-serve plans land later this year. For now, billing is handled directly so we can tailor the right
            fit for your club.{' '}
            <a
              href="mailto:hello@getscrimly.com"
              className="font-semibold text-[#3d6010] hover:underline"
            >
              hello@getscrimly.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
